import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    LabelList
} from 'recharts';
import { FiArrowLeft } from 'react-icons/fi';

const FOPaymentAnalytics = () => {
    const [yearlyData, setYearlyData] = useState([]);
    const [monthlyData, setMonthlyData] = useState([]);
    const [orderList, setOrderList] = useState([]);
    const [loading, setLoading] = useState({
        yearly: true,
        monthly: false,
        list: false
    });
    const [error, setError] = useState('');
    const [view, setView] = useState('yearly'); // yearly, monthly, list
    const [selectedYear, setSelectedYear] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(null);

    // Fetch yearly order data
    const fetchYearlyData = async () => {
        try {
            setLoading(prev => ({ ...prev, yearly: true }));
            setError('');
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/foorders/analytics/yearly`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            if (response.data.success) {
                setYearlyData(response.data.data);
            }
        } catch (err) {
            setError(`Failed to fetch yearly data: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(prev => ({ ...prev, yearly: false }));
        }
    };

    // Fetch monthly data for a specific year
    const fetchMonthlyData = async (year) => {
        try {
            setLoading(prev => ({ ...prev, monthly: true }));
            setError('');
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/foorders/analytics/monthly/${year}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            if (response.data.success) {
                setMonthlyData(response.data.data);
            }
        } catch (err) {
            setError(`Failed to fetch monthly data: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(prev => ({ ...prev, monthly: false }));
        }
    };

    // Fetch order list for a specific year and month
    const fetchOrderList = async (year, month) => {
        try {
            setLoading(prev => ({ ...prev, list: true }));
            setError('');
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/foorders/analytics/list/${year}/${month}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            if (response.data.success) {
                setOrderList(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching order list:', err);
            setError(`Failed to fetch order list: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(prev => ({ ...prev, list: false }));
        }
    };

    useEffect(() => {
        fetchYearlyData();
    }, []);

    const handleYearClick = async (year) => {
        setSelectedYear(year);
        setView('monthly');
        await fetchMonthlyData(year);
    };

    const handleMonthClick = async (year, month) => {
        // Convert month name back to number (1-12)
        const monthNames = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];
        const monthNumber = monthNames.indexOf(month) + 1;
        
        setSelectedMonth(month);
        setView('list');
        await fetchOrderList(year, monthNumber);
    };

    const handleBack = () => {
        if (view === 'list') {
            setView('monthly');
            setSelectedMonth(null);
        } else if (view === 'monthly') {
            setView('yearly');
            setSelectedYear(null);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-SG', {
            style: 'currency',
            currency: 'SGD'
        }).format(value);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-SG');
    };

    const renderYearlyChart = () => (
        <ResponsiveContainer width="100%" height={400}>
            <BarChart 
                data={yearlyData}
                margin={{ top: 40, right: 30, left: 20, bottom: 5 }}
            >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" />
                <YAxis 
                    tickFormatter={(value) => formatCurrency(value)}
                    width={100}
                    domain={[0, 'auto']}
                />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Bar
                    dataKey="totalAmount"
                    fill="#8884d8"
                    name="Total Profit (SGD)"
                    onClick={(data) => handleYearClick(data.year)}
                    cursor="pointer"
                >
                    <LabelList
                        dataKey="totalAmount"
                        position="top"
                        formatter={(value) => formatCurrency(value)}
                        style={{ fill: '#666' }}
                    />
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );

    const renderMonthlyChart = () => (
        <ResponsiveContainer width="100%" height={400}>
            <BarChart 
                data={monthlyData}
                margin={{ top: 40, right: 30, left: 20, bottom: 5 }}
            >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis 
                    tickFormatter={(value) => formatCurrency(value)}
                    width={100}
                    domain={[0, 'auto']}
                />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Bar
                    dataKey="totalAmount"
                    fill="#82ca9d"
                    name="Monthly Profit (SGD)"
                    onClick={(data) => handleMonthClick(selectedYear, data.month)}
                    cursor="pointer"
                >
                    <LabelList
                        dataKey="totalAmount"
                        position="top"
                        formatter={(value) => formatCurrency(value)}
                        style={{ fill: '#666' }}
                    />
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );

    const renderOrderList = () => (
        <div className="mt-4">
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Order Date
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Purchase Code
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Customer
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Total Sales
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Total Profit (SGD)
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Margin
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {orderList.length > 0 ? (
                            orderList.map((order) => {
                                const totalSellingPrice = order.items && order.items.length > 0
                                    ? order.items.reduce((sum, item) => sum + (parseFloat(item.sellingPrice) || 0), 0)
                                    : 0;
                                const creditNoteTotal = (order.creditNotes && Array.isArray(order.creditNotes))
                                    ? order.creditNotes.reduce((sum, cn) => sum + (parseFloat(cn.amountSGD) || 0), 0)
                                    : 0;
                                const totalSales = (order.totalSales !== undefined && order.totalSales !== null)
                                    ? order.totalSales
                                    : (totalSellingPrice - (parseFloat(order.discount) || 0) + (parseFloat(order.deliveryFees) || 0) - creditNoteTotal);
                                
                                return (
                                    <tr key={order._id}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {formatDate(order.earliestOrderDate)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="font-medium">{order.purchaseCode}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            {order.customer ? (
                                                <div>
                                                    <div className="font-medium">{order.customer.customerID}</div>
                                                    <div className="text-sm text-gray-500">{order.customer.Name}</div>
                                                </div>
                                            ) : 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {formatCurrency(totalSales)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap font-medium text-green-600">
                                            {formatCurrency(order.totalProfit || 0)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {order.margin !== undefined && order.margin !== null
                                                ? `${order.margin.toFixed(2)}%`
                                                : 'N/A'}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                                    No orders found for this period
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );

    const renderLoading = () => (
        <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
    );

    // Calculate total amount across all years
    const totalAmount = yearlyData.reduce((sum, year) => sum + (year.totalAmount || 0), 0);

    return (
        <div className="bg-white rounded-lg shadow w-full">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold px-8 py-8">
                    GZ Payment Analytics <span style={{ color: 'red' }}>({formatCurrency(totalAmount)})</span>
                </h2>
                {view !== 'yearly' && (
                    <button
                        onClick={handleBack}
                        className="flex items-center text-blue-600 hover:text-blue-800 mr-8"
                    >
                        <FiArrowLeft className="mr-2" />
                        Back
                    </button>
                )}
            </div>

            {error && (
                <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md mx-8">
                    {error}
                </div>
            )}

            {view === 'yearly' && (loading.yearly ? renderLoading() : renderYearlyChart())}
            {view === 'monthly' && (
                <>
                    <h3 className="text-lg font-medium mb-4 px-8">
                        Monthly Orders for {selectedYear}{' '}
                        <span className="text-red-600">
                            ({formatCurrency(monthlyData.reduce((sum, month) => sum + (month.totalAmount || 0), 0))})
                        </span>
                    </h3>
                    {loading.monthly ? renderLoading() : renderMonthlyChart()}
                </>
            )}
            {view === 'list' && (
                <>
                    <h3 className="text-lg font-medium mb-4 px-8">
                        Orders for {selectedMonth} {selectedYear}{' '}
                        <span className="text-red-600">
                            ({formatCurrency(orderList.reduce((sum, order) => sum + (order.totalProfit || 0), 0))})
                        </span>
                    </h3>
                    {loading.list ? renderLoading() : renderOrderList()}
                </>
            )}
        </div>
    );
};

export default FOPaymentAnalytics;
